import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import "../style/SignUp.css";
// import CalendarConnect from '../components/scheduling/CalendarConnect';

interface FubConnection {
  id: string;
  isConnected: boolean;
  accountId: string | null;
  allowedIPs: string | null;
  connectedAt: Date | null;
  lastSyncAt: Date | null;
}

const ConnectFub: React.FC = () => {
  const navigate = useNavigate();
  const [connection, setConnection] = useState<FubConnection | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  // const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const [formData, setFormData] = useState({
    apiKey: ''
  });

  useEffect(() => {
    fetchConnection();
  }, []);

  const fetchConnection = async () => {
    try {
      setLoading(true);
      const connectionData = await apiService.getFubConnection();
      setConnection(connectionData);
    } catch (error: any) {
      console.error('Failed to fetch FUB connection:', error);
      setError('Failed to load connection status');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
    setSuccess('');
  };

  /* const handleTestConnection = async () => {
    if (!formData.apiKey || !formData.accountId || !formData.xSystemKey) {
      setError('Please fill in API Key, Account ID, and X-System Key to test the connection');
      return;
    }

    try {
      setTesting(true);
      setError('');
      const result = await apiService.testFubConnection({
        apiKey: formData.apiKey,
        accountId: formData.accountId,
        xSystemKey: formData.xSystemKey
      });

      if (result.valid) {
        setSuccess('Connection test successful! Your credentials are valid.');
      } else {
        setError('Connection test failed. Please check your credentials.');
      }
    } catch (error: any) {
      setError('Connection test failed. Please verify your credentials and try again.');
    } finally {
      setTesting(false);
    }
  }; */

  const handleConnect = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.apiKey) {
      setError('API Key is required');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      const connectionData = await apiService.connectFub({ apiKey: formData.apiKey });
      setConnection(connectionData);
      setSuccess('FUB account connected successfully!');

      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (error: any) {
      const status = error?.response?.status;
      const msg = error?.response?.data?.message;
      // Surface explicit duplicate/used key messages from backend; otherwise show invalid credentials
      if (status === 400 && typeof msg === 'string' && msg.toLowerCase().includes('already')) {
        setError(msg);
      } else if (status === 400 && typeof msg === 'string') {
        setError(msg);
      } else {
        setError('Failed to connect FUB account. Please check your credentials.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect your FUB account? This will stop data synchronization.')) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      await apiService.disconnectFub();
      setConnection(null);
      setSuccess('FUB account disconnected successfully.');
      setFormData({
        apiKey: ''
      });
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to disconnect FUB account.');
    } finally {
      setSubmitting(false);
    }
  };

  // const handleSkip = () => {
  //   // For now, we'll allow users to skip but they won't have full functionality
  //   navigate('/dashboard');
  // };

  if (loading) {
    return (
      <div className="min-h-screen bg-lightestGray flex items-center justify-center">
        <div className="flex items-center gap-3">
          <i className="fa-solid fa-spinner fa-spin text-darkBlue text-xl"></i>
          <span className="text-darkGray text-lg">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-lightestGray py-12 px-4 overflow-y-auto">
      <div className="flex justify-center">
        <div className="w-[600px] p-8 rounded-2xl border border-lighterGray bg-white shadow-lg h-fit">
          <div className="flex items-center justify-between pb-2">
            <h2 className="text-h1 font-bold text-darkestSlate">Connect Your FUB Account</h2>
            <button
              type="button"
              onClick={() => setShowGuide(true)}
              className="px-3 py-1.5 text-sm border border-lighterGray rounded-lg text-darkGray hover:bg-lightestGray"
            >
              How to get API key
            </button>
          </div>
          {/* <p className="text-h3 text-darkGray mb-8">
            To use MyHomey, you must connect your personal Follow Up Boss (FUB) account.
          </p> */}

          {connection?.isConnected ? (
            // Connected State
            <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <h3 className="text-h3 font-semibold text-green-800">
                  Your FUB account is successfully connected
                </h3>
              </div>
              <div className="text-sm text-green-700 space-y-1">
                <p>Account ID: {connection.accountId ? `***${connection.accountId.slice(-4)}` : 'Hidden'}</p>
                <p>Connected: {connection.connectedAt ? new Date(connection.connectedAt).toLocaleDateString() : 'Unknown'}</p>
              </div>
              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="flex-1 text-h3 font-semibold text-white h-10 rounded-3xl bg-darkBlue hover:bg-mediumGray"
                >
                  Go to Dashboard
                </button>
                <button
                  onClick={handleDisconnect}
                  disabled={submitting}
                  className="px-4 text-h4 font-semibold text-red-600 h-10 rounded-3xl border border-red-300 hover:bg-red-50 disabled:opacity-50"
                >
                  {submitting ? 'Disconnecting...' : 'Disconnect'}
                </button>
              </div>
            </div>
          ) : (
            // Not Connected - Show Form
            <form className="space-y-5" onSubmit={handleConnect}>
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label htmlFor="apiKey" className="text-h4 font-medium text-darkGray pb-1 block">
                    API Key *
                  </label>
                  {/* keep compact height; details moved to the guide */}
                  <input
                    type="password"
                    id="apiKey"
                    value={formData.apiKey}
                    onChange={(e) => handleInputChange('apiKey', e.target.value)}
                    className="h-12 px-4 py-3 rounded-xl bg-lightestGray border border-lighterGray w-full focus:border-darkBlue focus:outline-none"
                    placeholder="API Key"
                    required
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-exclamation-triangle text-red-500"></i>
                    <span className="text-red-700 text-sm">{error}</span>
                  </div>
                </div>
              )}

              {success && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-check-circle text-green-500"></i>
                    <span className="text-green-700 text-sm">{success}</span>
                  </div>
                </div>
              )}

              <div className="flex gap-4">
                {/* <button
                  type="button"
                  onClick={handleTestConnection}
                  disabled={testing || !formData.apiKey || !formData.accountId || !formData.xSystemKey}
                  className="flex-1 text-h3 font-semibold text-darkestSlate h-12 rounded-3xl border border-lighterGray hover:bg-lightestGray disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {testing ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                      Testing...
                    </>
                  ) : (
                    'Test Connection'
                  )}
                </button> */}
                <button
                  type="submit"
                  disabled={submitting}
                  className={`flex-1 text-h3 font-semibold text-white h-12 rounded-3xl ${
                    submitting ? 'bg-gray cursor-not-allowed' : 'bg-darkBlue hover:bg-mediumGray'
                  }`}
                >
                  {submitting ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                      Connecting...
                    </>
                  ) : (
                    'Connect FUB'
                  )}
                </button>
              </div>

              {/* <div className="text-center">
                <button
                  type="button"
                  onClick={handleSkip}
                  className="text-h4 text-mediumGray hover:text-darkGray"
                >
                  Skip for now
                </button>
              </div> */}
            </form>
          )}
          {/* <div className="mt-6">
            <h3 className="text-h3 font-semibold text-darkestSlate mb-2">Calendar Integration</h3>
            <p className="text-darkGray mb-3">Connect your Google Calendar to enable availability and meeting scheduling.</p>
            <CalendarConnect /> */}
            {/* Optional: show quick availability picker once connected */}
          {/* </div> */}
          {/* Guide Modal */}
          {showGuide && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
              <div className="w-[600px] max-w-[95vw] bg-white rounded-2xl shadow-xl border border-lighterGray p-5">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-h3 font-semibold text-darkestSlate">How to get API key</h4>
                  <button onClick={() => setShowGuide(false)} className="text-mediumGray hover:text-darkGray">✕</button>
                </div>
                <div className="text-sm text-darkGray max-h-[60vh] overflow-auto pr-1">
                  <ol className="list-decimal pl-5 space-y-2">
                    <li>Log into your Follow Up Boss account.</li>
                    <li>Go to the <span className="font-semibold">Admin</span> section.</li>
                    <li>Navigate to <span className="font-semibold">More</span> → <span className="font-semibold">API</span>.</li>
                    <li>Click the <span className="font-semibold">Create API Key</span> button.</li>
                    <li>Give your API key a descriptive name (e.g., the name of this integration).</li>
                    <li>The system will generate and display your API key. <span className="font-semibold">Copy it immediately</span> — it will only be shown once.</li>
                    <li>Paste the key into the <span className="font-semibold">API Key</span> field in this application.</li>
                    <li>Click <span className="font-semibold">Connect FUB</span> to finish.</li>
                  </ol>
                </div>
                <div className="mt-4 text-right">
                  <button onClick={() => setShowGuide(false)} className="px-4 py-2 bg-darkBlue text-white rounded-lg">Got it</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConnectFub;
